import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useWorkspaceRoster } from "../hooks/useWorkspaceRoster";
import { api } from "../api/client";
import { PRIORITY_COLORS as PRIORITY_CFG, PRIORITY_RANK } from "../utils/priorityColors";
import { isTaskOverdue } from "../utils/taskTime"; // Block D20 Part A: overdue in the user's STORED timezone

// ── Design tokens ─────────────────────────────────────────────────────────────
const NAVY = "#0C2D48";
const FONT = "'Raleway', sans-serif";

const STATUS_CFG = {
  todo:       { label: "To Do",       color: "#5b3fa0", bg: "rgba(91,63,160,0.09)",  border: "rgba(91,63,160,0.25)"  },
  inProgress: { label: "In Progress", color: "#f59e0b", bg: "rgba(245,158,11,0.09)", border: "rgba(245,158,11,0.3)"  },
  done:       { label: "Completed",   color: "#22c55e", bg: "rgba(34,197,94,0.09)",  border: "rgba(34,197,94,0.3)"   },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatus(t) {
  if (t.completed)  return "done";
  if (t.inProgress) return "inProgress";
  return "todo";
}

// Block D20 Part A: compute overdue in the user's STORED timezone (set on render below),
// so the badge agrees with the Overdue filter and chat regardless of the browser's timezone.
function isOverdue(t) { return isTaskOverdue(t, isOverdue.tz); }

function fmtDate(dt) {
  if (!dt || isNaN(new Date(dt).getTime())) return null;
  const d    = new Date(dt);
  const diff = Math.round((d - new Date()) / 86400000);
  if (diff === 0)  return "Today";
  if (diff === 1)  return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function statusCycle(s) {
  return s === "todo" ? "inProgress" : s === "inProgress" ? "done" : "todo";
}
function statusToUpdates(s) {
  if (s === "done")       return { completed: true,  inProgress: false };
  if (s === "inProgress") return { completed: false, inProgress: true  };
  return                         { completed: false, inProgress: false };
}

function avatarBg(name, userColor) {
  if (userColor) return userColor;
  const p = ["#5b3fa0","#0ea5e9","#f59e0b","#22c55e","#ec4899","#8b5cf6","#14b8a6","#f97316"];
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (name.charCodeAt(i) + ((h << 5) - h)) | 0;
  return p[Math.abs(h) % p.length];
}
function initials(name = "?") {
  return name.trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

// ── Shared atoms ──────────────────────────────────────────────────────────────
function Avatar({ name, color, size = 26 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: avatarBg(name, color), color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, fontFamily: "'Poppins', sans-serif",
    }}>
      {initials(name)}
    </div>
  );
}

function PriorityDot({ priority }) {
  const color = (PRIORITY_CFG[priority] || PRIORITY_CFG.Medium).color;
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />;
}

function StatusBadge({ status, onClick }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.todo;
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      title="Click to advance status"
      style={{
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        borderRadius: 6, padding: "0.15rem 0.55rem", fontSize: "0.7rem", fontWeight: 700,
        cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap", flexShrink: 0,
      }}
    >
      {cfg.label}
    </button>
  );
}

function ProjectChip({ name, color }) {
  return (
    <span style={{
      fontSize: "0.7rem", color: color ? "#333" : "#666",
      background: color ? color + "22" : "#f0f4f8", border: `1px solid ${color || "#dde6ef"}`,
      borderRadius: 5, padding: "0.1rem 0.4rem", fontWeight: 600,
      maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0,
    }}>
      {name}
    </span>
  );
}

// ── Filter dropdown ───────────────────────────────────────────────────────────
function FilterDropdown({ triggerLabel, value, options, onChange, allLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function outside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  const selected = options.find(o => o.value === value) || null;
  const isActive = value !== null && value !== undefined;

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: "0.4rem",
          padding: "0.42rem 0.7rem", borderRadius: 8, cursor: "pointer",
          border: isActive ? `1.5px solid ${NAVY}` : "1px solid #e0e0e0",
          background: isActive ? "rgba(12,45,72,0.06)" : "#f8f8f8",
          color: NAVY, fontSize: "0.82rem", fontWeight: isActive ? 700 : 500,
          fontFamily: FONT, whiteSpace: "nowrap",
        }}
      >
        {selected?.avatar && <Avatar name={selected.label} color={selected.color} size={18} />}
        {selected?.dot && (
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: selected.dot, flexShrink: 0 }} />
        )}
        <span>{selected ? selected.label : triggerLabel}</span>
        <span style={{ fontSize: "0.6rem", color: "#aaa", marginLeft: "0.1rem" }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 200,
          background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10,
          padding: "0.35rem", minWidth: 190,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        }}>
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: "0.5rem",
              padding: "0.42rem 0.6rem", border: "none", borderRadius: 7,
              background: !isActive ? "rgba(12,45,72,0.07)" : "transparent",
              color: NAVY, fontSize: "0.82rem", fontWeight: !isActive ? 700 : 500,
              cursor: "pointer", fontFamily: FONT, textAlign: "left",
            }}
          >
            {allLabel}
          </button>
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "0.5rem",
                padding: "0.42rem 0.6rem", border: "none", borderRadius: 7,
                background: value === opt.value ? "rgba(12,45,72,0.07)" : "transparent",
                color: NAVY, fontSize: "0.82rem", fontWeight: value === opt.value ? 700 : 500,
                cursor: "pointer", fontFamily: FONT, textAlign: "left",
              }}
            >
              {opt.avatar && <Avatar name={opt.label} color={opt.color} size={20} />}
              {opt.dot && <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt.dot, flexShrink: 0 }} />}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Timer button ──────────────────────────────────────────────────────────────
function TimerBtn({ taskId, task, activeTimers, onToggle, loggedMinutes }) {
  const running = !!activeTimers[taskId];
  const secs    = activeTimers[taskId]?.elapsed || 0;
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }} onClick={e => e.stopPropagation()}>
      <button onClick={() => onToggle(taskId, task)} style={{ background: running ? "rgba(233,106,106,0.1)" : "rgba(12,45,72,0.06)", border: `1px solid ${running ? "#E96A6A" : "#ddd"}`, color: running ? "#E96A6A" : "#666", borderRadius: 5, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
        {running ? "⏹ Stop" : "▶ Start"}
      </button>
      {secs > 0 && <span style={{ fontSize: "0.7rem", color: "#999", fontFamily: "monospace" }}>{mm}:{ss}</span>}
      {!running && (loggedMinutes?.[String(taskId)] || 0) > 0 && (
        <span style={{ fontSize: "0.65rem", color: "#aaa" }}>
          {Math.floor((loggedMinutes[String(taskId)] || 0) / 60) > 0 ? `${Math.floor((loggedMinutes[String(taskId)] || 0) / 60)}h ` : ""}
          {(loggedMinutes[String(taskId)] || 0) % 60 > 0 ? `${(loggedMinutes[String(taskId)] || 0) % 60}m` : ""} logged
        </span>
      )}
    </div>
  );
}

// ── Client work toast ─────────────────────────────────────────────────────────
function ClientWorkToast({ task, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, background: "#fff", border: "1px solid #fbbf24", borderLeft: "4px solid #f59e0b", borderRadius: 10, padding: "0.75rem 1rem", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontFamily: FONT, maxWidth: 320, display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⏱️</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400e", marginBottom: "0.15rem" }}>This is client work</div>
        <div style={{ fontSize: "0.8rem", color: "#555" }}>Don't forget to start your timer for <strong>{task?.title}</strong>.</div>
      </div>
      <button onClick={onDismiss} style={{ background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "0.85rem", padding: 0, lineHeight: 1 }}>✕</button>
    </div>
  );
}

// ── Mobile viewport hook (640px breakpoint) ───────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = e => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

// ── Task row ──────────────────────────────────────────────────────────────────
function TaskRow({ task, onView, onMove, allUsers, showAvatar, isPro, activeTimers, onTimerToggle, loggedMinutes }) {
  const status     = getStatus(task);
  const overdue    = isOverdue(task);
  const pCfg       = PRIORITY_CFG[task.priority] || PRIORITY_CFG.Medium;
  const assigneeId = task.assignedTeam?.[0] ?? task.userId ?? task.createdBy;
  const assignee   = allUsers.find(u => u.id === assigneeId);
  const isMobile   = useIsMobile();
  const running    = !!activeTimers[task.id];

  if (isMobile) {
    const dateStr = (task.datetime || task.deadline)
      ? (overdue ? "⚠ " : "") + fmtDate(task.deadline || task.datetime)
      : null;
    return (
      <div
        onClick={() => onView(task)}
        onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
        style={{ padding: "0.55rem 0.75rem", borderBottom: "1px solid #f2f2f2", background: "#fff", cursor: "pointer", borderLeft: `3px solid ${pCfg.color}`, opacity: task.completed ? 0.65 : 1, transition: "background 0.1s", fontFamily: FONT }}
      >
        {/* Line 1: priority dot + title, wraps up to 2 lines */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.45rem", marginBottom: "0.35rem" }}>
          <span style={{ marginTop: "0.22rem", flexShrink: 0, lineHeight: 1 }}>
            <PriorityDot priority={task.priority} />
          </span>
          <span style={{ fontWeight: 600, fontSize: "0.88rem", color: NAVY, minWidth: 0, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", textDecoration: task.completed ? "line-through" : "none", lineHeight: 1.35 }}>
            {task.title}
          </span>
        </div>
        {/* Line 2: date/overdue left, icon actions right */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", paddingLeft: "1.15rem" }}>
          {dateStr && (
            <span style={{ fontSize: "0.72rem", color: overdue ? "#dc2626" : "#888", fontWeight: overdue ? 700 : 400, whiteSpace: "nowrap" }}>
              {dateStr}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {isPro && !task.completed && (
            <div onClick={e => e.stopPropagation()}>
              <button
                onClick={() => onTimerToggle(task.id, task)}
                style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: running ? "rgba(233,106,106,0.1)" : "rgba(12,45,72,0.06)", border: `1px solid ${running ? "#E96A6A" : "#ddd"}`, color: running ? "#E96A6A" : "#666", borderRadius: 8, cursor: "pointer", fontSize: "1rem", flexShrink: 0 }}
              >
                {running ? "⏹" : "▶"}
              </button>
            </div>
          )}
          {!task.completed && (
            <button
              onClick={e => { e.stopPropagation(); onMove(task.id, { completed: true, inProgress: false }); }}
              style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", color: "#16a34a", borderRadius: 8, cursor: "pointer", fontSize: "1.1rem", flexShrink: 0 }}
            >
              ✓
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onView(task)}
      onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
      style={{
        display: "flex", alignItems: "center", gap: "0.65rem",
        padding: "0.6rem 1.25rem", borderBottom: "1px solid #f2f2f2",
        background: "#fff", cursor: "pointer",
        borderLeft: `3px solid ${pCfg.color}`,
        opacity: task.completed ? 0.65 : 1, transition: "background 0.1s", fontFamily: FONT,
      }}
    >
      <PriorityDot priority={task.priority} />
      <span style={{
        flex: 1, fontWeight: 600, fontSize: "0.88rem", color: NAVY,
        textDecoration: task.completed ? "line-through" : "none",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
      }}>
        {task.title}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", flexShrink: 0 }}>
        {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
        {(task.datetime || task.deadline) && (
          <span style={{
            fontSize: "0.72rem", color: overdue ? "#dc2626" : "#888",
            fontWeight: overdue ? 700 : 400, whiteSpace: "nowrap",
          }}>
            {overdue && "⚠ "}{fmtDate(task.deadline || task.datetime)}
          </span>
        )}
        {showAvatar && assignee && <Avatar name={assignee.name} color={assignee.color} size={22} />}
        {isPro && !task.completed && <TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes} />}
        {!task.completed && <button onClick={e => { e.stopPropagation(); onMove(task.id, { completed: true, inProgress: false }); }} style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", color: "#16a34a", borderRadius: 5, padding: "0.1rem 0.45rem", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>✓ Done</button>}
        <StatusBadge status={status} onClick={() => onMove(task.id, statusToUpdates(statusCycle(status)))} />
      </div>
    </div>
  );
}

// ── Collapsible group section ─────────────────────────────────────────────────
function GroupSection({ icon, title, subtitle, count, tasks, onView, onMove, allUsers, showAvatar, defaultOpen = true, isPro, activeTimers, onTimerToggle, loggedMinutes }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "0.35rem" }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "0.65rem",
          padding: "0.65rem 1.25rem", background: "#f7f8fc",
          border: "none", borderBottom: "1px solid #e8e8e8",
          cursor: "pointer", fontFamily: FONT, textAlign: "left",
        }}
      >
        {icon}
        <span style={{ fontWeight: 700, fontSize: "0.88rem", color: NAVY, flex: 1 }}>{title}</span>
        {subtitle && <span style={{ fontSize: "0.75rem", color: "#999" }}>{subtitle}</span>}
        <span style={{ background: NAVY, color: "#fff", borderRadius: 10, padding: "0.05rem 0.5rem", fontSize: "0.72rem", fontWeight: 700 }}>
          {count}
        </span>
        <span style={{ color: "#aaa", fontSize: "0.75rem", transform: open ? "rotate(0)" : "rotate(-90deg)", transition: "transform 0.15s" }}>▼</span>
      </button>
      {open && (
        <div style={{ borderBottom: "2px solid #f0f0f0" }}>
          {tasks.length === 0
            ? <p style={{ margin: 0, padding: "0.75rem 1.25rem", color: "#aaa", fontSize: "0.82rem", fontStyle: "italic" }}>No tasks here.</p>
            : tasks.map(t => <TaskRow key={t.id} task={t} onView={onView} onMove={onMove} allUsers={allUsers} showAvatar={showAvatar} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} loggedMinutes={loggedMinutes} />)
          }
        </div>
      )}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ onAddTask }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "5rem 2rem", gap: "1rem", textAlign: "center",
    }}>
      <span style={{ fontSize: "3rem" }}>📋</span>
      <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#555", fontFamily: "'Poppins', sans-serif" }}>
        No tasks found
      </div>
      <div style={{ fontSize: "0.88rem", color: "#aaa", fontFamily: FONT, maxWidth: 280 }}>
        Try adjusting your filters, or add a new task to get started.
      </div>
      <button
        onClick={onAddTask}
        style={{
          padding: "0.55rem 1.4rem", background: "#E4E69B", border: "none",
          borderRadius: 8, fontWeight: 700, fontSize: "0.9rem",
          cursor: "pointer", fontFamily: FONT, color: "#333", marginTop: "0.25rem",
        }}
      >
        + Add a Task
      </button>
    </div>
  );
}

// ── Main AllTasksView ─────────────────────────────────────────────────────────
export default function AllTasksView({ tasks, onViewTask, onMoveTask, onAddTask, currentUser, taskTypes }) {
  isOverdue.tz = currentUser?.timezone; // Block D20 Part A: stored timezone for the overdue helper
  const { members: allUsers } = useWorkspaceRoster();
  const isPro = ["Pro", "Team", "Enterprise"].includes(currentUser?.plan);

  const isMobile = useIsMobile();

  const [search,         setSearch]         = useState("");
  const [viewMode,       setViewMode]       = useState("person");
  const [sortBy,         setSortBy]         = useState("priority");
  const [pill,           setPill]           = useState("all");
  const [memberFilter,   setMemberFilter]   = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [statusFilter,   setStatusFilter]   = useState(null);
  const [activeTimers,   setActiveTimers]   = useState({});
  const [loggedMinutes,  setLoggedMinutes]  = useState({});
  const [clientToast,    setClientToast]    = useState(null);
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const timerRefs = useRef({});

  useEffect(() => () => Object.values(timerRefs.current).forEach(clearInterval), []);

  async function saveTimeEntry(taskId, task, elapsed) {
    const durationMins = Math.round(elapsed / 60);
    if (durationMins < 1) return;
    const startDt = new Date(Date.now() - elapsed * 1000);
    try {
      await api('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          taskId: String(taskId),
          projectId: task?.projectId ?? null,
          startTime: startDt.toISOString(),
          endTime: new Date().toISOString(),
          duration: durationMins,
          note: null,
        }),
      });
      setLoggedMinutes(prev => ({ ...prev, [String(taskId)]: (prev[String(taskId)] || 0) + durationMins }));
    } catch (_) {}
  }

  function toggleTimer(taskId, task) {
    setActiveTimers(prev => {
      if (prev[taskId]) {
        clearInterval(timerRefs.current[taskId]);
        delete timerRefs.current[taskId];
        const elapsed = prev[taskId].elapsed;
        saveTimeEntry(taskId, task, elapsed);
        const next = { ...prev }; delete next[taskId]; return next;
      }
      const start = Date.now();
      timerRefs.current[taskId] = setInterval(() => {
        setActiveTimers(p => p[taskId] ? { ...p, [taskId]: { ...p[taskId], elapsed: Math.floor((Date.now() - start) / 1000) } } : p);
      }, 1000);
      return { ...prev, [taskId]: { start, elapsed: 0 } };
    });
  }

  function handleViewTask(task) {
    if (isPro && task?.projectId && !activeTimers[task.id]) {
      setClientToast(task);
    }
    onViewTask(task);
  }

  // ── Dropdown option lists ──
  const memberOptions = useMemo(() => {
    const ids = new Set();
    tasks.forEach(t => {
      if (t.assignedTeam?.length) t.assignedTeam.forEach(id => ids.add(id));
      else if (t.userId)    ids.add(t.userId);
      else if (t.createdBy) ids.add(t.createdBy);
    });
    return allUsers
      .filter(u => ids.has(u.id))
      .map(u => ({ value: u.id, label: u.name, color: u.color, avatar: true }));
  }, [tasks, allUsers]);

  const priorityOptions = ["Critical","High","Medium","Low"].map(p => ({
    value: p, label: p, dot: PRIORITY_CFG[p].color,
  }));

  const statusOptions = Object.entries(STATUS_CFG).map(([k, v]) => ({
    value: k, label: v.label, dot: v.color,
  }));

  // ── Filter + sort ──
  const filtered = useMemo(() => {
    let list = [...tasks];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title?.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q));
    }
    if (pill === "overdue")    list = list.filter(isOverdue);
    if (pill === "high")       list = list.filter(t => t.priority === "High" || t.priority === "Critical");
    if (pill === "unassigned") list = list.filter(t => !t.assignedTeam?.length && !t.userId && !t.createdBy);
    if (memberFilter != null)  list = list.filter(t => t.assignedTeam?.includes(memberFilter) || t.userId === memberFilter || t.createdBy === memberFilter);
    if (priorityFilter)        list = list.filter(t => t.priority === priorityFilter);
    if (statusFilter)          list = list.filter(t => getStatus(t) === statusFilter);
    list.sort((a, b) => {
      if (sortBy === "priority") {
        const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
      }
      if (sortBy === "dueDate")  return new Date(a.deadline || a.datetime || 0) - new Date(b.deadline || b.datetime || 0);
      if (sortBy === "created")  return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      return new Date(a.deadline || a.datetime || 0) - new Date(b.deadline || b.datetime || 0);
    });
    return list;
  }, [tasks, search, pill, memberFilter, priorityFilter, statusFilter, sortBy]);

  const totalActive = tasks.filter(t => !t.completed).length;
  const hasFilters  = memberFilter != null || priorityFilter || statusFilter || pill !== "all" || search.trim();
  const activeFilterCount = [
    memberFilter != null,
    !!priorityFilter,
    !!statusFilter,
    sortBy !== "priority",
    viewMode !== "person",
  ].filter(Boolean).length;

  // ── Group helpers ──
  function groupByPerson(list) {
    const unassigned = [], byId = {};
    list.forEach(t => {
      const aid = t.assignedTeam?.[0] ?? t.userId ?? t.createdBy ?? null;
      if (aid == null) { unassigned.push(t); return; }
      if (!byId[aid]) byId[aid] = [];
      byId[aid].push(t);
    });
    return { unassigned, byId };
  }

  function groupByProject(list) {
    const noProject = [], byName = {};
    list.forEach(t => {
      if (!t.projectName) { noProject.push(t); return; }
      if (!byName[t.projectName]) byName[t.projectName] = [];
      byName[t.projectName].push(t);
    });
    return { noProject, byName };
  }

  const { unassigned: pUnassigned, byId: pById }     = viewMode === "person"  ? groupByPerson(filtered)  : { unassigned: [], byId: {}   };
  const { noProject: prjNone,      byName: prjByName } = viewMode === "project" ? groupByProject(filtered) : { noProject: [], byName: {} };

  const CHIP_DEFS = [
    { id: "all",        label: "All"             },
    { id: "overdue",    label: "⚠ Overdue"       },
    { id: "high",       label: "🔴 High Priority" },
    { id: "unassigned", label: "👤 Unassigned"   },
  ];

  const selectStyle = {
    width: "100%", padding: "0.42rem 0.7rem", border: "1px solid #e0e0e0",
    borderRadius: 8, fontSize: "0.82rem", fontFamily: FONT, color: NAVY,
    background: "#f8f8f8", cursor: "pointer", outline: "none",
  };

  return (
    <div className="all-tasks-container" style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 4rem)", fontFamily: FONT,
      overflow: "hidden", background: "#fafafa",
    }}>

      {isMobile ? (
        /* ── Mobile header ── */
        <div style={{ padding: "0.6rem 0.75rem", borderBottom: "1px solid #e8e8e8", background: "#fff", flexShrink: 0 }}>

          {/* Row 1: compact title + Filters button */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.45rem" }}>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: NAVY, fontFamily: "'Poppins', sans-serif", flex: 1 }}>
              All Tasks
              <span style={{ fontSize: "0.72rem", color: "#999", fontWeight: 400, marginLeft: "0.4rem" }}>{totalActive} active</span>
            </span>
            <button
              onClick={() => setFiltersOpen(v => !v)}
              style={{
                padding: "0.35rem 0.75rem", borderRadius: 8, cursor: "pointer", fontFamily: FONT,
                border: activeFilterCount > 0 ? `1.5px solid ${NAVY}` : "1px solid #e0e0e0",
                background: activeFilterCount > 0 ? NAVY : "#f8f8f8",
                color: activeFilterCount > 0 ? "#fff" : NAVY,
                fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "0.3rem",
              }}
            >
              {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
              <span style={{ fontSize: "0.6rem", opacity: 0.7, transform: filtersOpen ? "rotate(180deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.15s" }}>▼</span>
            </button>
          </div>

          {/* Row 2: search */}
          <div style={{ position: "relative", marginBottom: "0.45rem" }}>
            <span style={{ position: "absolute", left: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: "0.82rem", pointerEvents: "none" }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tasks…"
              style={{
                paddingLeft: "2rem", paddingRight: "0.75rem", paddingTop: "0.42rem", paddingBottom: "0.42rem",
                border: "1px solid #e0e0e0", borderRadius: 8, fontSize: "0.82rem",
                fontFamily: FONT, outline: "none", width: "100%", background: "#f8f8f8", color: NAVY,
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Row 3: quick-filter chips — horizontally scrollable */}
          <div style={{ display: "flex", gap: "0.35rem", overflowX: "auto", paddingBottom: "0.1rem", scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {CHIP_DEFS.map(f => (
              <button
                key={f.id}
                onClick={() => setPill(f.id)}
                style={{
                  padding: "0.22rem 0.75rem", borderRadius: 20, cursor: "pointer", flexShrink: 0,
                  border: pill === f.id ? `1.5px solid ${NAVY}` : "1px solid #ddd",
                  background: pill === f.id ? NAVY : "#fff",
                  color: pill === f.id ? "#fff" : "#555",
                  fontSize: "0.78rem", fontWeight: pill === f.id ? 700 : 500, fontFamily: FONT,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Expanding filters panel */}
          {filtersOpen && (
            <div style={{ marginTop: "0.6rem", paddingTop: "0.6rem", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: "0.45rem" }}>
              {memberOptions.length > 0 && (
                <select
                  value={memberFilter ?? ""}
                  onChange={e => setMemberFilter(e.target.value || null)}
                  style={selectStyle}
                >
                  <option value="">All Members</option>
                  {memberOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              <select
                value={priorityFilter ?? ""}
                onChange={e => setPriorityFilter(e.target.value || null)}
                style={selectStyle}
              >
                <option value="">All Priorities</option>
                {priorityOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={statusFilter ?? ""}
                onChange={e => setStatusFilter(e.target.value || null)}
                style={selectStyle}
              >
                <option value="">All Statuses</option>
                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
                <option value="priority">Sort: Priority</option>
                <option value="dueDate">Sort: Due Date</option>
                <option value="created">Sort: Created</option>
              </select>
              {/* View toggle */}
              <div style={{ display: "flex", border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden" }}>
                {[{ id: "list", label: "≡ List" }, { id: "person", label: "👤 Person" }, { id: "project", label: "📁 Project" }].map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setViewMode(id)}
                    style={{
                      flex: 1, padding: "0.42rem 0", border: "none",
                      borderRight: id !== "project" ? "1px solid #e0e0e0" : "none",
                      background: viewMode === id ? NAVY : "#fff",
                      color: viewMode === id ? "#fff" : "#555",
                      fontWeight: viewMode === id ? 700 : 500,
                      fontSize: "0.78rem", cursor: "pointer", fontFamily: FONT,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {hasFilters && (
                <button
                  onClick={() => { setMemberFilter(null); setPriorityFilter(null); setStatusFilter(null); setPill("all"); setSearch(""); }}
                  style={{ background: "none", border: "1px solid #E96A6A", color: "#E96A6A", borderRadius: 8, padding: "0.42rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
                >
                  ✕ Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ── Desktop top bar ── */}
          <div style={{
            padding: "0.85rem 1.25rem 0.75rem", borderBottom: "1px solid #e8e8e8",
            background: "#fff", flexShrink: 0,
            display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap",
          }}>
            <div style={{ marginRight: "0.25rem" }}>
              <div style={{ fontWeight: 700, fontSize: "1.2rem", color: NAVY, fontFamily: "'Poppins', sans-serif", lineHeight: 1.2 }}>All Tasks</div>
              <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "0.05rem" }}>{totalActive} active · {filtered.length} shown</div>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ position: "relative", minWidth: 0, maxWidth: 180, flex: "0 1 180px" }}>
              <span style={{ position: "absolute", left: "0.6rem", top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: "0.82rem", pointerEvents: "none" }}>🔍</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tasks…"
                style={{ paddingLeft: "2rem", paddingRight: "0.75rem", paddingTop: "0.42rem", paddingBottom: "0.42rem", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: "0.82rem", fontFamily: FONT, outline: "none", width: "100%", background: "#f8f8f8", color: NAVY }}
              />
            </div>
            <FilterDropdown triggerLabel="All Members" allLabel="All Members" value={memberFilter} options={memberOptions} onChange={setMemberFilter} />
            <FilterDropdown triggerLabel="All Priorities" allLabel="All Priorities" value={priorityFilter} options={priorityOptions} onChange={setPriorityFilter} />
            <FilterDropdown triggerLabel="All Statuses" allLabel="All Statuses" value={statusFilter} options={statusOptions} onChange={setStatusFilter} />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: "0.42rem 0.7rem", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: "0.82rem", fontFamily: FONT, color: NAVY, background: "#f8f8f8", cursor: "pointer", outline: "none", flexShrink: 0 }}>
              <option value="priority">Sort: Priority</option>
              <option value="dueDate">Sort: Due Date</option>
              <option value="created">Sort: Created</option>
            </select>
            <div style={{ display: "flex", border: "1px solid #e0e0e0", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
              {[{ id: "list", label: "≡ List" }, { id: "person", label: "👤 Person" }, { id: "project", label: "📁 Project" }].map(({ id, label }) => (
                <button key={id} onClick={() => setViewMode(id)} style={{ padding: "0.42rem 0.75rem", border: "none", borderRight: id !== "project" ? "1px solid #e0e0e0" : "none", background: viewMode === id ? NAVY : "#fff", color: viewMode === id ? "#fff" : "#555", fontWeight: viewMode === id ? 700 : 500, fontSize: "0.78rem", cursor: "pointer", fontFamily: FONT }}>
                  {label}
                </button>
              ))}
            </div>
            {hasFilters && (
              <button onClick={() => { setMemberFilter(null); setPriorityFilter(null); setStatusFilter(null); setPill("all"); setSearch(""); }} style={{ background: "none", border: "1px solid #E96A6A", color: "#E96A6A", borderRadius: 8, padding: "0.42rem 0.7rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT, flexShrink: 0 }}>
                ✕ Clear
              </button>
            )}
            <button onClick={onAddTask} style={{ padding: "0.48rem 1.05rem", background: "#E4E69B", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT, color: "#333", flexShrink: 0 }}>
              + Add Task
            </button>
          </div>

          {/* ── Desktop filter pills ── */}
          <div style={{ padding: "0.55rem 1.25rem", borderBottom: "1px solid #f0f0f0", display: "flex", gap: "0.4rem", flexShrink: 0, background: "#fff", flexWrap: "wrap" }}>
            {CHIP_DEFS.map(f => (
              <button key={f.id} onClick={() => setPill(f.id)} style={{ padding: "0.25rem 0.85rem", borderRadius: 20, cursor: "pointer", border: pill === f.id ? `1.5px solid ${NAVY}` : "1px solid #ddd", background: pill === f.id ? NAVY : "#fff", color: pill === f.id ? "#fff" : "#555", fontSize: "0.8rem", fontWeight: pill === f.id ? 700 : 500, fontFamily: FONT }}>
                {f.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Task body ── */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {filtered.length === 0 && <EmptyState onAddTask={onAddTask} />}

        {/* LIST */}
        {viewMode === "list" && filtered.length > 0 && filtered.map(t => (
          <TaskRow key={t.id} task={t} onView={handleViewTask} onMove={onMoveTask} allUsers={allUsers} showAvatar isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes} />
        ))}

        {/* GROUP BY PERSON */}
        {viewMode === "person" && filtered.length > 0 && (
          <>
            <GroupSection
              icon={<span style={{ fontSize: "1.1rem" }}>📌</span>}
              title="Unassigned"
              count={pUnassigned.length}
              tasks={pUnassigned}
              onView={handleViewTask}
              onMove={onMoveTask}
              allUsers={allUsers}
              showAvatar={false}
              defaultOpen={pUnassigned.length > 0}
              isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes}
            />
            {allUsers.filter(u => pById[u.id]?.length > 0).map(u => (
              <GroupSection
                key={u.id}
                icon={<Avatar name={u.name} color={u.color} size={26} />}
                title={u.name}
                subtitle={u.jobTitle || u.role || ""}
                count={pById[u.id].length}
                tasks={pById[u.id]}
                onView={handleViewTask}
                onMove={onMoveTask}
                allUsers={allUsers}
                showAvatar={false}
                isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes}
              />
            ))}
            {/* Assignee ids present on tasks but not in the active-workspace roster
                (e.g. the current user, or a former member) — rendered by id/name here. */}
            {Object.keys(pById)
              .filter(id => id && pById[id]?.length > 0 && !allUsers.find(u => u.id === id))
              .map(id => {
                const name = id === currentUser?.id ? (currentUser?.name || 'You') : id;
                const color = id === currentUser?.id ? currentUser?.color : undefined;
                return (
                  <GroupSection
                    key={id}
                    icon={<Avatar name={name} color={color} size={26} />}
                    title={name}
                    count={pById[id].length}
                    tasks={pById[id]}
                    onView={handleViewTask}
                    onMove={onMoveTask}
                    allUsers={allUsers}
                    showAvatar={false}
                    isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes}
                  />
                );
              })}
          </>
        )}

        {/* GROUP BY PROJECT */}
        {viewMode === "project" && filtered.length > 0 && (
          <>
            {prjNone.length > 0 && (
              <GroupSection
                icon={<span style={{ fontSize: "1.1rem" }}>📂</span>}
                title="No Project"
                count={prjNone.length}
                tasks={prjNone}
                onView={handleViewTask}
                onMove={onMoveTask}
                allUsers={allUsers}
                showAvatar
                defaultOpen={false}
                isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes}
              />
            )}
            {Object.entries(prjByName).sort(([a],[b]) => a.localeCompare(b)).map(([name, ts]) => (
              <GroupSection
                key={name}
                icon={<span style={{ fontSize: "1.1rem" }}>📁</span>}
                title={name}
                count={ts.length}
                tasks={ts}
                onView={handleViewTask}
                onMove={onMoveTask}
                allUsers={allUsers}
                showAvatar
                isPro={isPro} activeTimers={activeTimers} onTimerToggle={toggleTimer} loggedMinutes={loggedMinutes}
              />
            ))}
          </>
        )}
      </div>

      {/* Mobile FAB — Add Task, bottom-left (chat bubble lives bottom-right) */}
      {isMobile && (
        <button
          onClick={onAddTask}
          style={{
            position: "fixed", bottom: "5rem", left: "1rem",
            width: 48, height: 48, borderRadius: "50%",
            background: "#E4E69B", border: "none", color: "#333",
            fontSize: "1.6rem", fontWeight: 700, lineHeight: 1,
            cursor: "pointer", fontFamily: FONT,
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 999,
          }}
          aria-label="Add Task"
        >
          +
        </button>
      )}

      {/* Client work toast */}
      {clientToast && <ClientWorkToast task={clientToast} onDismiss={() => setClientToast(null)} />}
    </div>
  );
}
